import { type Client, type EnclaveSpecification, proto } from "@decentriq/core";
import { type ab_media_request as ddcAbMediaRequest } from "ddc";
import * as forge from "node-forge";
import * as uuid from "uuid";
import { useApiCore } from "contexts";
import { type PublishMediaDataRoomInput } from "features/mediaDataRoom/models";
import { normalizeEmail } from "utils/String";

interface MediaEnclaveSpecifications {
  driverEnclaveSpecification: ddcAbMediaRequest.EnclaveSpecificationV0;
  pythonEnclaveSpecification: ddcAbMediaRequest.EnclaveSpecificationV0;
}

export function calculateAttestationHash(
  enclaveSpecification: ddcAbMediaRequest.EnclaveSpecificationV0
): string {
  const messageDigest = forge.md.sha256.create();
  const attestationSpecificationEncoded = forge.util.binary.base64.decode(
    enclaveSpecification.attestationProtoBase64
  );
  messageDigest.update(
    forge.util.binary.raw.encode(attestationSpecificationEncoded)
  );
  return messageDigest.digest().toHex();
}

function enclaveSpecificationToMediaEnclaveSpecification(
  enclaveSpecification: EnclaveSpecification
): ddcAbMediaRequest.EnclaveSpecificationV0 {
  const attestationProtoBase64 = forge.util.binary.base64.encode(
    proto.attestation.AttestationSpecification.encodeDelimited(
      enclaveSpecification.proto
    ).finish()
  );

  return {
    attestationProtoBase64,
    id: enclaveSpecification.name,
    workerProtocol: Math.max(...enclaveSpecification.workerProtocols),
  };
}

export async function retrieveLatestMediaEnclaveSpecifications(
  client: Client
): Promise<MediaEnclaveSpecifications> {
  const allSpecifications = await client.getEnclaveSpecifications();
  let driverEnclaveSpecification: EnclaveSpecification | null = null;
  let pythonEnclaveSpecification: EnclaveSpecification | null = null;
  for (const specification of allSpecifications) {
    if (specification.name === "decentriq.driver") {
      if (
        driverEnclaveSpecification === null ||
        parseInt(driverEnclaveSpecification.version) <
          parseInt(specification.version)
      ) {
        driverEnclaveSpecification = specification;
      }
    } else if (specification.name === "decentriq.python-ml-worker-32-64") {
      if (
        pythonEnclaveSpecification === null ||
        parseInt(pythonEnclaveSpecification.version) <
          parseInt(specification.version)
      ) {
        pythonEnclaveSpecification = specification;
      }
    }
  }
  if (driverEnclaveSpecification === null) {
    throw new Error("Could not find 'decentriq.driver' enclave specification");
  }
  if (pythonEnclaveSpecification === null) {
    // Try to find a backup nitro specification for the python node
    // TODO remove once nitro is completely decommissioned
    for (const specification of allSpecifications) {
      if (specification.name === "decentriq.python-ml-worker") {
        if (
          pythonEnclaveSpecification === null ||
          parseInt(pythonEnclaveSpecification.version) <
            parseInt(specification.version)
        ) {
          pythonEnclaveSpecification = specification;
        }
      }
    }
    if (pythonEnclaveSpecification === null) {
      throw new Error(
        "Could not find 'decentriq.python-ml-worker-32-64' or 'decentriq.python-ml-worker' enclave specification"
      );
    }
  }
  return {
    driverEnclaveSpecification: enclaveSpecificationToMediaEnclaveSpecification(
      driverEnclaveSpecification
    ),
    pythonEnclaveSpecification: enclaveSpecificationToMediaEnclaveSpecification(
      pythonEnclaveSpecification
    ),
  };
}

const usePublishMediaDataRoom = () => {
  const { client: apiCoreClient, sessionManager } = useApiCore();
  const publish = async (
    input: PublishMediaDataRoomInput
  ): Promise<{
    id: string;
    driverAttestationHash: string;
  }> => {
    const enclaveSpecifications =
      await retrieveLatestMediaEnclaveSpecifications(apiCoreClient);
    const driverAttestationHash = calculateAttestationHash(
      enclaveSpecifications.driverEnclaveSpecification
    );
    const [authenticationRootCertificatePem, session] = await Promise.all([
      apiCoreClient.decentriqCaRootCertificate(),
      sessionManager.get({ driverAttestationHash }),
    ]);
    const dataRoom = session.compiler.abMedia.create({
      v7: {
        advertiserEmails: input.advertiserEmails.map(normalizeEmail),
        agencyEmails: input.agencyEmails.map(normalizeEmail),
        authenticationRootCertificatePem: new TextDecoder().decode(
          authenticationRootCertificatePem
        ),
        dataPartnerEmails: input.dataPartnerEmails.map(normalizeEmail),
        driverEnclaveSpecification:
          enclaveSpecifications.driverEnclaveSpecification,
        enableAdvertiserAudienceDownload:
          input?.enableAdvertiserAudienceDownload,
        enableDataPartner: input.dataPartnerEmails.length > 0,
        enableDebugMode: false,
        enableHideAbsoluteValuesFromInsights:
          input?.hideAbsoluteValuesFromInsights,
        enableInsights: input?.enableInsights,
        enableLookalikeAudiences: input?.enableLookalike,
        enableRemarketing: input?.enableRemarketing,
        enableRuleBasedAudiences: input?.enableRuleBasedAudiences,
        hashMatchingIdWith: input.matchingIdHashingAlgorithm,
        id: uuid.v4(),
        mainAdvertiserEmail: normalizeEmail(input.mainAdvertiserEmail),
        mainPublisherEmail: normalizeEmail(input.mainPublisherEmail),
        matchingIdFormat: input.matchingIdFormat,
        modelEvaluation: {
          postScopeMerge: ["ROC_CURVE"],
          preScopeMerge: [],
        },
        name: input.name,
        observerEmails: input.observerEmails.map(normalizeEmail),
        publisherEmails: input.publisherEmails.map(normalizeEmail),
        pythonEnclaveSpecification:
          enclaveSpecifications.pythonEnclaveSpecification,
      },
    });
    const request: ddcAbMediaRequest.AbMediaRequest = {
      publishDataRoom: {
        dataRoom,
        requirePassword: false,
        showOrganizationLogo: false,
      },
    };
    const resp = await session.sendAbMediaRequest(request);
    if (!("publishDataRoom" in resp)) {
      throw new Error("Expected publishDataRoom response");
    }
    return {
      driverAttestationHash,
      id: resp?.publishDataRoom.dataRoomId,
    };
  };
  return {
    publish,
  };
};

export default usePublishMediaDataRoom;
